import type { Pinia } from 'pinia';
import { SimpleObserver } from '@common/libs/observer';
import type { Caller, CallerOptions } from './Caller';

/**
 * Registre des clients HTTP.
 *
 * Chaque application déclare ses callers au démarrage (`registerCaller`) et les
 * consomme ensuite par nom. Les observateurs globaux sont branchés une fois pour
 * toutes, quel que soit le caller instancié ensuite.
 */
const callers: Record<string, { factory: (pinia: Pinia) => Caller; instance?: Caller }> = {};

const startObserver = new SimpleObserver();
const beforeObserver = new SimpleObserver();
const successObserver = new SimpleObserver();
const errorObserver = new SimpleObserver();
const completeObserver = new SimpleObserver();

export function registerCaller(name: string, factory: (pinia: Pinia) => Caller): void {
	callers[name] = { factory };
}

export function getCaller(name: string, pinia: Pinia): Caller {
	const entry = callers[name];
	if (!entry) {
		throw new Error(`Le caller « ${name} » n'est pas enregistré.`);
	}
	if (!entry.instance) {
		entry.instance = entry.factory(pinia);
		entry.instance.startObserver.subscribe((event) => startObserver.trigger(event));
		entry.instance.beforeObserver.subscribe((event) => beforeObserver.trigger(event));
		entry.instance.successObserver.subscribe((event) => successObserver.trigger(event));
		entry.instance.errorObserver.subscribe((event) => errorObserver.trigger(event));
		entry.instance.completeObserver.subscribe((event) => completeObserver.trigger(event));
	}
	return entry.instance;
}

export type CallerEvent = { url: string; options: CallerOptions };

export const subscribeCallerStart = (cb: (event: CallerEvent) => void) => startObserver.subscribe(cb);
export const subscribeCallerBefore = (cb: (event: CallerEvent) => void) => beforeObserver.subscribe(cb);
export const subscribeCallerSuccess = (cb: (event: CallerEvent & { result: any }) => void) => successObserver.subscribe(cb);
export const subscribeCallerError = (cb: (error: any) => void) => errorObserver.subscribe(cb);
export const subscribeCallerComplete = (cb: (event: { url: string }) => void) => completeObserver.subscribe(cb);
